import React from "react";
import { BlockIcon, DeleteIcon, DownloadIcon, EditIcon } from "assets";
import cx from "classnames";
import _sortBy from "lodash/sortBy";
import { generatePath } from "react-router-dom";
import {
  withRouter,
  RouterProps,
  Button,
  SectionContainer,
} from "component-library";
import { IVideoModel } from "shared-types";
import { UserRoleManager, UserRoles } from "app/core";
import { captureExceptionEvent } from "shared-services";
import {
  BASE_PATH,
  ADD_VIDEO_FULL_PATH,
  EDIT_VIDEO_FULL_PATH,
} from "app/config/routes";
import { VideoPlayerContainer } from "mediaModules/media/components/VideoPlayerContainer";
import withMediaData, {
  IWithMediaDataInjectedProps,
  withMediaAssetType,
} from "mediaModules/media/hoc/withMediaData";
import {
  MediaAsset,
  MediaAssetType,
  TranscodeStatus,
} from "mediaModules/media/models";
import { scrollRefToTop } from "mediaModules/media/utils/page";

import {
  IGlobalContextProps,
  withGlobalContext,
} from "shared/contexts/GlobalContext";
import compose from "shared/utils/compose";
import { PromiseContainer } from "shared/utils/promise";
import { GlobalEventTypes } from "contexts/GlobalContext";
import {
  ITranslationContextProps,
  Translator,
  withTranslationF,
} from "contexts/TranslationContext";

import {
  Breadcrumbs,
  IBreadcrumbsContent,
} from "components/common/Breadcrumbs";

import { EditVideoListForm } from "./components/EditVideoListForm";
import {
  getAlertMessage,
  getDisplayLimitAlert,
  getSectionContainerProps,
  IVideoPageContent,
  IVideoPageContentKeys,
  PopupTypes,
} from "./VideoPageContent";

import "./VideoPage.scss";
import { IActionButtonProps } from "component-library/src/components/Atoms/ActionButton/ActionButton";
import { IBreadcrumbsContentKeys } from "components/common/Breadcrumbs/Breadcrumbs";

export interface IVideoPageProps
  extends
    ITranslationContextProps,
    IGlobalContextProps,
    RouterProps,
    IWithMediaDataInjectedProps {}

interface IVideoPageState {
  content: IVideoPageContent;
}

export class VideoPage extends React.Component<
  IVideoPageProps,
  IVideoPageState
> {
  public static getDerivedStateFromProps(
    nextProps: IVideoPageProps,
    prevState: IVideoPageState
  ) {
    let stateUpdates = {};

    if (
      nextProps.translator.version !==
      nextProps.translator.getTranslationVersion(prevState.content)
    ) {
      const content = VideoPage.mapTranslation(nextProps.translator);

      stateUpdates = { content };
    }

    return stateUpdates;
  }

  private static mapTranslation = (translator: Translator) =>
    translator.compileTranslationObject<IVideoPageContent>(
      IVideoPageContentKeys,
      "common"
    );

  private deletePromise: PromiseContainer<any> = new PromiseContainer();
  private pageRef: React.RefObject<HTMLDivElement> =
    React.createRef<HTMLDivElement>();

  constructor(props: Readonly<IVideoPageProps>) {
    super(props);

    this.state = {
      content: VideoPage.mapTranslation(props.translator),
    };
  }

  public render() {
    const { content } = this.state;
    const {
      storageSpace,
      mediaAssets,
      isMediaMetadataLoading,
      areMediaObjectsLoading,
      isPatching,
      changeMediaAssetsOrder,
    } = this.props;

    const breadcrumbsContent: IBreadcrumbsContent =
      this.getBreadcrumbsTranslation();
    const sectionContainerProps = getSectionContainerProps(
      content,
      this.closePage
    );
    const sortedMediaAssets = _sortBy(mediaAssets, ["sortOrder"]);

    return (
      <div ref={this.pageRef}>
        <Breadcrumbs content={breadcrumbsContent} />
        <div className="g-content-area p-video-page">
          <div className="g-col-lg-12 p-video-page__container">
            <SectionContainer {...sectionContainerProps}>
              <div className="p-video-page__content">
                <EditVideoListForm
                  content={content.editVideoListForm}
                  showreelList={sortedMediaAssets}
                  storageSpace={storageSpace}
                  editShowreel={this.redirectToEditVideoPage}
                  hideShowreels={this.hideShowreels}
                  playbackVideo={this.playbackVideo}
                  deleteShowreels={this.deleteShowreelsAlert}
                  changeMediaAssetsOrder={changeMediaAssetsOrder}
                  addNewShowreel={this.addNewShowreel}
                  downloadShowreel={this.downloadShowreel}
                  isPatching={isPatching}
                  isMediaMetadataLoading={isMediaMetadataLoading}
                  areMediaObjectsLoading={areMediaObjectsLoading}
                />
                {this.renderFormButtons()}
              </div>
            </SectionContainer>
          </div>
        </div>
      </div>
    );
  }

  private getBreadcrumbsTranslation = () =>
    this.props.translator
      .getTranslator("breadcrumbs", true)
      .createTranslationObject<IBreadcrumbsContent>(IBreadcrumbsContentKeys);

  private renderFormButtons = () => {
    const { areMediaObjectsLoading } = this.props;
    const { content } = this.state;

    return (
      <div className="p-video-page__buttons">
        <div className="p-video-page__button">
          <Button
            type="primary"
            onClick={this.returnToCVEditor}
            text={content.cancelButton}
          />
        </div>
        <div className="p-video-page__button">
          <Button
            className={cx("p-video-page__add-showreel", {
              "p-video-page__add-showreel--disabled": areMediaObjectsLoading,
            })}
            type="secondary"
            onClick={this.addNewShowreel}
            text={content.addButton}
          />
        </div>
      </div>
    );
  };

  private redirectToEditVideoPage = ({ id }: MediaAsset) => {
    scrollRefToTop(this.pageRef);
    this.props.router.navigate(generatePath(EDIT_VIDEO_FULL_PATH, { id }));
  };

  private returnToCVEditor = () => {
    const { agentCVLink } = this.state.content;

    window.location.assign(this.getCVLink(BASE_PATH, agentCVLink));
  };

  private getCVLink = (performerCVLink: string, agentCVLink: string) => {
    return UserRoleManager.isInRole(UserRoles.agent)
      ? agentCVLink
      : performerCVLink;
  };

  private closePage = () => this.props.router.navigate(BASE_PATH);

  private playbackVideo = (showreel: MediaAsset) => {
    if (showreel.transcodeStatus === TranscodeStatus.Complete) {
      const controls = this.getPlayerPanel(showreel);
      const video = this.mapVideoModel(showreel);

      const videoPlayer = (
        <VideoPlayerContainer
          playerConfig={{
            autoPlay: true,
            hasCloseButton: true,
            videoList: [video],
            selectedVideo: video,
            selectVideo: () => undefined,
            close: this.closeVideoPopupWrapper(),
          }}
          controlPanelButtons={controls}
        />
      );

      const popupProps = {
        hideCloseButton: true,
        fullHeight: true,
      };

      this.props.globalContext.notifyListener(GlobalEventTypes.showPopup, {
        content: videoPlayer,
        popupProps,
      });
    }
  };

  private hideShowreels = async (showreels: MediaAsset[]) => {
    const { content } = this.state;
    const { hideMediaAssets, globalContext } = this.props;

    const displayLimitReachedCallback = () => {
      const message = getDisplayLimitAlert(content.displayLimitAlert, () =>
        this.closePopup()
      );
      globalContext.notifyListener(
        GlobalEventTypes.notifyingGlobalAlert,
        message
      );
    };

    await hideMediaAssets(showreels, displayLimitReachedCallback);
  };

  private addNewShowreel = () => {
    const { content } = this.state;
    const { globalContext } = this.props;

    const message = getAlertMessage(
      content,
      PopupTypes.Copyright,
      this.redirectToAddNewVideoPage,
      this.closePopup,
      "notice"
    );
    globalContext.notifyListener(
      GlobalEventTypes.notifyingGlobalAlert,
      message
    );
  };

  private redirectToAddNewVideoPage = () => {
    this.closePopup();
    scrollRefToTop(this.pageRef);
    this.props.router.navigate(ADD_VIDEO_FULL_PATH);
  };

  private deleteShowreelsAlert = (deletedShowreels: MediaAsset[]) => {
    const { content } = this.state;

    const { globalContext, removeMediaAssets } = this.props;

    this.deletePromise.setPromise();
    this.deletePromise.promise
      .then(async () => await removeMediaAssets(deletedShowreels))
      .catch((error) => captureExceptionEvent(error));

    const closePopup = () => {
      this.deletePromise.reject("Popup is closed");
      this.closePopup();
    };

    const message = getAlertMessage(
      content,
      PopupTypes.Delete,
      this.deletePromise.resolve,
      closePopup
    );
    globalContext.notifyListener(
      GlobalEventTypes.notifyingGlobalAlert,
      message
    );

    return this.deletePromise.promise;
  };

  private downloadShowreel = (showreel: MediaAsset) =>
    (window.location.href = showreel.downloadLink);

  private getPlayerPanel = (showreel: MediaAsset) => {
    const { controls } = this.state.content.editVideoListForm;
    const buttons: (IActionButtonProps & { key: string | number })[] = [];

    buttons.push({
      key: "edit",
      svgIcon: <EditIcon />,
      label: controls.edit,
      onClick: this.closeVideoPopupWrapper(() => {
        this.redirectToEditVideoPage(showreel);
      }),
    });

    buttons.push({
      key: "delete",
      svgIcon: <DeleteIcon />,
      label: controls.delete,
      onClick: this.closeVideoPopupWrapper(() => {
        this.deleteShowreelsAlert([showreel]);
      }),
    });

    if (!UserRoleManager.isInRole(UserRoles.castingDirector)) {
      buttons.push({
        key: "hide",
        svgIcon: <BlockIcon />,
        label: controls.hide,
        onClick: this.closeVideoPopupWrapper(() => {
          this.hideShowreels([showreel]);
        }),
      });
    }

    buttons.push({
      key: "download1",
      svgIcon: <DownloadIcon />,
      label: controls.download,
      onClick: this.closeVideoPopupWrapper(() => {
        this.downloadShowreel(showreel);
      }),
    });

    return buttons;
  };

  private mapVideoModel = (showreel: MediaAsset) => {
    const {
      durationInMs,
      title,
      sources,
      id,
      thumbnails,
      selectedThumbnail,
      downloadLink,
    } = showreel;

    const video: IVideoModel = {
      id,
      sources,
      downloadLink,
      duration: durationInMs,
      fileName: title,
      previewImg: thumbnails[selectedThumbnail],
      poster: thumbnails[0],
      shareLink: downloadLink,
    };

    return video;
  };

  private closeVideoPopupWrapper =
    (wrappedFunction?: () => void | Promise<void>) => async () => {
      const result = wrappedFunction && wrappedFunction();

      if (result instanceof Promise) {
        await result;
      }

      this.props.globalContext.notifyListener(GlobalEventTypes.showPopup, {
        content: null,
      });
    };

  private closePopup = () =>
    this.props.globalContext.notifyListener(
      GlobalEventTypes.closeAllGlobalAlert
    );
}

export default compose(
  withTranslationF("editVideoPage"),
  withMediaAssetType(MediaAssetType.Video),
  withMediaData,
  withGlobalContext,
  withRouter
)(VideoPage) as React.ComponentType;
